import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Tag,
  Space,
  Typography,
  Button,
  DatePicker,
  Select,
  Spin,
  Row,
  Col,
  Divider,
  Tabs,
  Statistic,
  List,
  Alert,
  InputNumber,
  Modal,
  Descriptions,Input,
  message
} from "antd";
import {
  DownloadOutlined,
  FilterOutlined,
  SyncOutlined,
  UserOutlined,
  ShopOutlined,
  DashboardOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SearchOutlined
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import { getUserData } from "../../../hooks/Auth";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;
const user = getUserData();

const API_BASE_URL = "https://techcrew.pk/ulc/";

const SupplierLedger = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: [moment().startOf('month'), moment().endOf('month')],
    userId: null,
    userRole: null,
    transactionType: null,
    searchText: ''
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const fetchCommissionData = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: filters.dateRange[0].format('YYYY-MM-DD'),
        end_date: filters.dateRange[1].format('YYYY-MM-DD'),
        user_id: user?.user?.id,
        user_role: filters.userRole
      };

      const { data } = await axios.get(`${API_BASE_URL}Get_CommissionLedger.php`, { params });
      
      if (data.status.code === 200) {
        setData(data.data);
      } else {
        throw new Error(data.status.message);
      }
    } catch (error) {
      console.error("Failed to fetch commission data", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}Get_Users.php`);
      if (data.status.code === 200) {
        setUsers(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch users", error);
    }
  };

  const handleDateChange = (dates) => {
    setFilters(prev => ({
      ...prev,
      dateRange: dates
    }));
  };

  const handleUserChange = (value) => {
    setFilters(prev => ({
      ...prev,
      userId: value
    }));
  };

  const handleRoleChange = (value) => {
    setFilters(prev => ({
      ...prev,
      userRole: value
    }));
  };

  const handleTypeChange = (value) => {
    setFilters(prev => ({
      ...prev,
      transactionType: value
    }));
  };

  const handleSearch = (e) => {
    setFilters(prev => ({
      ...prev,
      searchText: e.target.value
    }));
  };

  const resetFilters = () => {
    setFilters({
      dateRange: [moment().startOf('month'), moment().endOf('month')],
      userId: null,
      userRole: null,
      transactionType: null,
      searchText: ''
    });
  };

  const exportToExcel = () => {
    message.success("Export feature will be implemented soon");
  };

  const viewTransactionDetails = (record) => {
    setSelectedTransaction(record);
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'transaction_date',
      key: 'date',
      render: date => moment(date).format('DD MMM YYYY HH:mm'),
      sorter: (a, b) => new Date(a.transaction_date) - new Date(b.transaction_date),
    },
    {
      title: 'User',
      dataIndex: 'user_name',
      key: 'user',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Tag color={record.user_role === 'admin' ? 'blue' : 'green'}>
            {record.user_role || 'N/A'}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Payment',
      key: 'payment',
      render: (_, record) => (
        <Text>
          {record.payment_id ? `#${record.payment_id}` : 'N/A'}
          {record.order_id && ` (Order #${record.order_id})`}
        </Text>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record) => (
        <Text strong type={record.transaction_type === 'payment' ? 'danger' : 'success'}>
          {record.transaction_type === 'payment' ? '-' : '+'}
          {parseFloat(amount).toLocaleString("en-US", {
            style: "currency",
            currency: "SAR",
          })}
        </Text>
      ),
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: 'Type',
      dataIndex: 'transaction_type',
      key: 'type',
      render: type => (
        <Tag color={type === 'receive' ? 'green' : 'red'}>
          {type.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Received', value: 'receive' },
        { text: 'Paid', value: 'payment' },
      ],
      onFilter: (value, record) => record.transaction_type === value,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button 
          icon={<SearchOutlined />}
          onClick={() => viewTransactionDetails(record)}
          size="small"
        />
      ),
    }
  ];

  useEffect(() => {
    fetchUsers();
    fetchCommissionData();
  }, [filters]);

  const filteredTransactions = data?.transactions?.filter(transaction => {
    // Apply search filter
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      const matches = 
        transaction.user_name.toLowerCase().includes(searchLower) ||
        (transaction.payment_id && transaction.payment_id.toString().includes(searchLower)) ||
        (transaction.order_id && transaction.order_id.toString().includes(searchLower)) ||
        transaction.notes.toLowerCase().includes(searchLower);
      
      if (!matches) return false;
    }
    
    // Apply transaction type filter
    if (filters.transactionType) {
      return transaction.transaction_type === filters.transactionType;
    }
    
    return true;
  }) || [];

  // Calculate summary data
  const summaryData = {
    totalReceived: filteredTransactions
      .filter(t => t.transaction_type === 'receive')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0),
    totalPaid: filteredTransactions
      .filter(t => t.transaction_type === 'payment')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0),
    netBalance: filteredTransactions
      .reduce((sum, t) => {
        const amount = parseFloat(t.amount);
        return t.transaction_type === 'receive' ? sum + amount : sum - amount;
      }, 0)
  };

  return (
    <div style={{ paddingLeft: "250px", paddingTop: "60px" }}>
      <Card
        title={<Title level={4} style={{ margin: 0 }}>Commission & Payment Ledger</Title>}
        bordered={false}
        extra={
          <Space>
            <Button 
              icon={<DownloadOutlined />}
              onClick={exportToExcel}
            >
              Export
            </Button>
            <Button 
              icon={<SyncOutlined />}
              onClick={fetchCommissionData}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        }
        style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={handleDateChange}
              allowClear={false}
            />
          </Col>
          {/* <Col span={5}>
            <Select
              style={{ width: '100%' }}
              placeholder="Filter by user"
              value={filters.userId}
              onChange={handleUserChange}
              allowClear
            >
              {users.map(user => (
                <Option key={user.user_id} value={user.user_id}>
                  {user.name} ({user.role})
                </Option>
              ))}
            </Select>
          </Col> */}
          <Col span={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Filter by role"
              value={filters.userRole}
              onChange={handleRoleChange}
              allowClear
            >
              <Option value="admin">Admin</Option>
              <Option value="supplier">Supplier</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Filter by type"
              value={filters.transactionType}
              onChange={handleTypeChange}
              allowClear
            >
              <Option value="receive">Received</Option>
              <Option value="payment">Paid</Option>
            </Select>
          </Col>
          <Col span={3}>
            <Button 
              onClick={resetFilters}
              icon={<FilterOutlined />}
              danger
            >
              Reset
            </Button>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Input
              placeholder="Search transactions..."
              prefix={<SearchOutlined />}
              value={filters.searchText}
              onChange={handleSearch}
              allowClear
            />
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Received"
                value={summaryData.totalReceived}
                precision={2}
                prefix={<ArrowDownOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
                suffix="SAR"
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Paid"
                value={summaryData.totalPaid}
                precision={2}
                prefix={<ArrowUpOutlined style={{ color: '#f5222d' }} />}
                valueStyle={{ color: '#f5222d' }}
                suffix="SAR"
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Net Balance"
                value={summaryData.netBalance}
                precision={2}
                prefix={<DollarOutlined />}
                valueStyle={{
                  color: summaryData.netBalance > 0 ? '#52c41a' : 
                        summaryData.netBalance < 0 ? '#f5222d' : '#000'
                }}
                suffix="SAR"
              />
            </Card>
          </Col>
        </Row>

        <Tabs defaultActiveKey="1">
          <TabPane
            tab={
              <span>
                <DashboardOutlined />
                All Transactions
              </span>
            }
            key="1"
          >
            <Table
              columns={columns}
              dataSource={filteredTransactions}
              rowKey="transaction_id"
              loading={loading}
              scroll={{ x: true }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} transactions`,
              }}
              rowClassName={(record) => 
                record.transaction_type === 'payment' ? 'payment-row' : 'receive-row'
              }
            />
          </TabPane>
          <TabPane
            tab={
              <span>
                <UserOutlined />
                Admin Transactions
              </span>
            }
            key="2"
          >
            <Table
              columns={columns}
              dataSource={filteredTransactions.filter(t => t.user_role === 'admin')}
              rowKey="transaction_id"
              loading={loading}
              scroll={{ x: true }}
            />
          </TabPane>
          <TabPane
            tab={
              <span>
                <ShopOutlined />
                Supplier Transactions
              </span>
            }
            key="3"
          >
            <Table
              columns={columns}
              dataSource={filteredTransactions.filter(t => t.user_role === 'supplier')}
              rowKey="transaction_id"
              loading={loading}
              scroll={{ x: true }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Transaction Detail Modal */}
      <Modal
        title={`Transaction #${selectedTransaction?.transaction_id}`}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedTransaction && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Date" span={2}>
              {moment(selectedTransaction.transaction_date).format('DD MMM YYYY HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label="User">
              <Text strong>{selectedTransaction.user_name}</Text>
              <br />
              <Tag color={selectedTransaction.user_role === 'admin' ? 'blue' : 'green'}>
                {selectedTransaction.user_role || 'N/A'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag color={selectedTransaction.transaction_type === 'receive' ? 'green' : 'red'}>
                {selectedTransaction.transaction_type.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              <Text strong type={selectedTransaction.transaction_type === 'payment' ? 'danger' : 'success'}>
                {selectedTransaction.transaction_type === 'payment' ? '-' : '+'}
                {parseFloat(selectedTransaction.amount).toLocaleString("en-US", {
                  style: "currency",
                  currency: "SAR",
                })}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Payment ID">
              {selectedTransaction.payment_id ? `#${selectedTransaction.payment_id}` : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Order ID">
              {selectedTransaction.order_id ? `#${selectedTransaction.order_id}` : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Notes" span={2}>
              {selectedTransaction.notes || 'No notes available'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <style jsx global>{`
        .ant-table-row.receive-row {
          background-color: #f6ffed;
        }
        .ant-table-row.payment-row {
          background-color: #fff1f0;
        }
      `}</style>
    </div>
  );
};

export default SupplierLedger;